'use server';

import fs from 'fs';
import path from 'path';
import { headers } from 'next/headers';

// In-memory rate limiting store: IP -> array of request timestamps
const rateLimitStore = new Map<string, number[]>();

function isRateLimited(ip: string, limit = 3, windowMs = 10 * 60 * 1000): boolean {
  const now = Date.now();
  const timestamps = rateLimitStore.get(ip) || [];
  
  // Filter out expired timestamps outside the sliding window
  const activeTimestamps = timestamps.filter(ts => now - ts < windowMs);
  
  if (activeTimestamps.length >= limit) {
    rateLimitStore.set(ip, activeTimestamps);
    return true;
  }
  
  activeTimestamps.push(now);
  rateLimitStore.set(ip, activeTimestamps);
  return false;
}

function hasSpamCharacteristics(text: string): boolean {
  if (!text) return false;
  // Flag excessive link payloads (typical automated spam pattern)
  const linkCount = (text.match(/https?:\/\//gi) || []).length;
  if (linkCount > 2) return true;
  
  // Flag obvious promotional Cyrillic injection in local Mexican niche sites
  const cyrillicRegex = /[\u0400-\u04FF]/;
  if (cyrillicRegex.test(text)) return true;
  
  return false;
}

export async function submitContactForm(formData: FormData) {
  try {
    const headersList = await headers();
    const forwardedFor = headersList.get('x-forwarded-for');
    const clientIp = forwardedFor ? forwardedFor.split(',')[0].trim() : '127.0.0.1';

    // 1. Honeypot Verification: invisible field must remain empty
    const honeypot = formData.get('corporate_fax_validation') as string;
    if (honeypot && honeypot.trim() !== '') {
      // Silently discard bot submission
      return { success: true, reason: 'honeypot_triggered' };
    }

    // 2. Time Trap Verification: human interaction must take at least 3 seconds
    const renderedAt = parseInt((formData.get('_form_timestamp') as string) || '0', 10);
    const elapsedSeconds = (Date.now() - renderedAt) / 1000;
    if (elapsedSeconds < 3 || elapsedSeconds > 86400) {
      return { success: true, reason: 'time_trap_triggered' };
    }

    // 3. Sliding Window Rate Limiting per IP
    if (isRateLimited(clientIp, 3, 10 * 60 * 1000)) {
      return { success: false, error: 'Rate limit exceeded. Please wait a few minutes.' };
    }

    // 4. Data Extraction & Sanitization
    const domain = (formData.get('domain') as string) || 'unknown';
    const url = (formData.get('url') as string) || '';
    const nombre = (formData.get('nombre') as string || '').slice(0, 100);
    const email = (formData.get('email') as string || '').slice(0, 150);
    const mensaje = (formData.get('mensaje') as string || '').slice(0, 2000);

    if (!nombre || !email || !mensaje) {
      return { success: false, error: 'All fields are mandatory.' };
    }

    if (hasSpamCharacteristics(mensaje) || hasSpamCharacteristics(nombre)) {
      return { success: true, reason: 'spam_heuristics_matched' };
    }

    // Safe payload write to lead queue
    const leadPayload = {
      type: 'CONTACTO',
      domain,
      url,
      clientIp,
      nombre,
      email,
      mensaje,
      timestamp: new Date().toISOString()
    };

    const targetQueuePath = path.join(process.cwd(), 'public', 'leads_queue.jsonl');
    fs.appendFileSync(targetQueuePath, JSON.stringify(leadPayload) + '\n', 'utf8');

    return { success: true };
  } catch (err) {
    return { success: false, error: 'Internal processing error' };
  }
}

export async function submitNewsletterForm(formData: FormData) {
  try {
    const headersList = await headers();
    const forwardedFor = headersList.get('x-forwarded-for');
    const clientIp = forwardedFor ? forwardedFor.split(',')[0].trim() : '127.0.0.1';

    // 1. Honeypot check
    const honeypot = formData.get('newsletter_subscriber_token') as string;
    if (honeypot && honeypot.trim() !== '') {
      return { success: true, reason: 'honeypot_triggered' };
    }

    // 2. Rate limit check
    if (isRateLimited(clientIp, 5, 10 * 60 * 1000)) {
      return { success: false, error: 'Too many requests.' };
    }

    const domain = (formData.get('domain') as string) || 'unknown';
    const email = (formData.get('email') as string || '').slice(0, 150);

    if (!email || !email.includes('@')) {
      return { success: false, error: 'Invalid email address.' };
    }

    const leadPayload = {
      type: 'NEWSLETTER',
      domain,
      clientIp,
      email,
      timestamp: new Date().toISOString()
    };

    const targetQueuePath = path.join(process.cwd(), 'public', 'leads_queue.jsonl');
    fs.appendFileSync(targetQueuePath, JSON.stringify(leadPayload) + '\n', 'utf8');

    return { success: true };
  } catch (err) {
    return { success: false, error: 'Internal processing error' };
  }
}
